import React, { useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FakeText, Card, IconButton } from '@totalsoft/rocket-ui';
import Indicator from '@mui/icons-material/BarChart';
import FinancialIndicatorComponent from './FinancialIndicatorComponent';
import { initialState, reducer } from '../../reducers/containerReducer';
import { useValidation, isValid } from '@totalsoft/pure-validations-react';
import { financialIndicatorValidator } from 'features/financialIndicators/validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { FINANCIAL_INDICATOR_QUERY, FINANCIAL_INDICATORS_STYLES_QUERY } from './queries';
import { ADD_UPDATE_FINANCIAL_INDICATOR } from './mutations';
import { FINANCIAL_INDICATOR_UPDATED } from './subscriptions';
import { useToast } from 'hooks/toasts/useToast';

const FinancialIndicatorContainer = ({ id, onChangeTitle, onRedirect }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [financialIndicatorValidation, validateFinancialIndicator] = useValidation(financialIndicatorValidator);

  const { data, loading: loadingHeader } = useQuery(FINANCIAL_INDICATOR_QUERY, {
    variables: { id },
    skip: id === undefined
  });

  const { data: styles, loading: financialIndicatorsStyleLoading } = useQuery(FINANCIAL_INDICATORS_STYLES_QUERY);

  const [addOrUpdateFinancialIndicator] = useMutation(ADD_UPDATE_FINANCIAL_INDICATOR);

  useSubscription(FINANCIAL_INDICATOR_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialIndicators.FinancialIndicatorUpdated'), 'success');
      if (id === undefined) onRedirect(data.financialIndicatorUpdated.financialIndicator.id);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedFinancialIndicators' });
      client.cache.gc();
    }
  });

  const onSave = useCallback(() => {
    // eslint-disable-next-line no-unused-vars
    const { changed, ...financialIndicator } = localState.details;
    if (!validateFinancialIndicator(localState.details)) return;
    dispatch({ type: 'OnSave' });
    return addOrUpdateFinancialIndicator({
      variables: { input: financialIndicator }
    });
  }, [addOrUpdateFinancialIndicator, localState.details, validateFinancialIndicator]);

  useEffect(() => {
    if (localState.details.name) {
      onChangeTitle(localState.details.name);
    }
  }, [localState.details.name, onChangeTitle]);

  useEffect(() => {
    if (!id) dispatch({ type: 'OnFinancialIndicatorAdd' });
    else if (data && data.financialIndicator) dispatch({ type: 'OnInitState', financialIndicator: data.financialIndicator });
  }, [data, id]);

  useEffect(() => {
    validateFinancialIndicator(localState.details);
  }, [localState.details, validateFinancialIndicator]);

  const loading = loadingHeader || financialIndicatorsStyleLoading;

  return (
    <Card
      icon={Indicator}
      iconColor='primary'
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('AutomaticStateTransitions.Save')}
          onClick={onSave}
          disabled={!localState.details.changed || !(financialIndicatorValidation |> isValid)}
        />
      }
    >
      {loading || localState === initialState ? (
        <FakeText lines={10} />
      ) : (
        <FinancialIndicatorComponent
          financialIndicator={localState.details}
          styles={styles ? styles.financialIndicatorStyles : []}
          dispatch={dispatch}
          validation={financialIndicatorValidation}
        />
      )}
    </Card>
  );
};

FinancialIndicatorContainer.propTypes = {
  id: PropTypes.number,
  onChangeTitle: PropTypes.func.isRequired,
  onRedirect: PropTypes.func
};

export default FinancialIndicatorContainer;
